import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import Layout from '../components/Layout';

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());

  const [showEventModal, setShowEventModal] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    event_type: 'reminder',
    start_date: '',
    description: '',
    property_id: '',
    unit_id: ''
  });
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);

  const fetchEvents = useCallback(async () => {
    try {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const { data, error } = await supabase
        .from('calendar_events')
        .select(`
          *,
          properties(name),
          units(unit_number),
          residents(first_name, last_name)
        `)
        .gte('start_date', startOfMonth.toISOString())
        .lte('start_date', endOfMonth.toISOString())
        .order('start_date');

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  }, [currentDate]);

  const fetchProperties = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('id, name');
      if (error) throw error;
      setProperties(data || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchProperties();
  }, [fetchEvents, fetchProperties]);

  const fetchUnits = async (propertyId) => {
    if (!propertyId) return;
    try {
      const { data, error } = await supabase
        .from('units')
        .select('id, unit_number')
        .eq('property_id', propertyId);
      if (error) throw error;
      setUnits(data || []);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const getEventsForDate = (day) => {
    if (!day) return [];
    const dateStr = new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toISOString().split('T')[0];
    return events.filter(event => event.start_date.startsWith(dateStr));
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('calendar_events')
        .insert([{
          ...newEvent,
          start_date: new Date(newEvent.start_date).toISOString()
        }]);

      if (error) throw error;
      
      setShowEventModal(false);
      setNewEvent({
        title: '',
        event_type: 'reminder',
        start_date: '',
        description: '',
        property_id: '',
        unit_id: ''
      });
      fetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
    }
  };

  const getEventTypeColor = (type) => {
    const colors = {
      lease_start: '#10b981',
      lease_end: '#ef4444',
      rent_due: '#f59e0b',
      maintenance: '#3b82f6',
      inspection: '#8b5cf6',
      showing: '#6366f1',
      court_date: '#b91c1c',
      reminder: '#6b7280'
    };
    return colors[type] || '#6b7280';
  };

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Calendar</h1>
          <button
            onClick={() => setShowEventModal(true)}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Add Event
          </button>
        </div>

        {/* Calendar */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <button
              onClick={() => navigateMonth(-1)}
              style={{ padding: '8px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '18px' }}
            >
              ←
            </button>
            <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h2>
            <button
              onClick={() => navigateMonth(1)}
              style={{ padding: '8px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '18px' }}
            >
              →
            </button>
          </div>

          {/* Days of Week */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', marginBottom: '10px' }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} style={{ padding: '8px', textAlign: 'center', fontWeight: '500', color: '#666' }}>
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px' }}>
            {getDaysInMonth().map((day, index) => (
              <div
                key={index}
                style={{
                  minHeight: '100px',
                  padding: '4px',
                  border: '1px solid #e5e7eb',
                  cursor: day ? 'pointer' : 'default',
                  backgroundColor: day ? '#fff' : '#f9fafb'
                }}
                onClick={() => day && console.log('Selected day:', day)}
              >
                {day && (
                  <>
                    <div style={{ fontWeight: '500', fontSize: '14px', marginBottom: '4px' }}>{day}</div>
                    <div>
                      {getEventsForDate(day).slice(0, 2).map(event => (
                        <div
                          key={event.id}
                          style={{
                            fontSize: '10px',
                            padding: '2px 4px',
                            borderRadius: '2px',
                            color: 'white',
                            marginBottom: '2px',
                            backgroundColor: getEventTypeColor(event.event_type).replace('bg-', '#'),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={event.title}
                        >
                          {event.title}
                        </div>
                      ))}
                      {getEventsForDate(day).length > 2 && (
                        <div style={{ fontSize: '10px', color: '#666' }}>
                          +{getEventsForDate(day).length - 2} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Event Modal */}
        {showEventModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', width: '400px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Add New Event</h3>
            <form onSubmit={handleCreateEvent}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>Title</label>
                  <input
                    type="text"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                    required
                  />
                </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Event Type</label>
                <select
                  value={newEvent.event_type}
                  onChange={(e) => setNewEvent({...newEvent, event_type: e.target.value})}
                  className="w-full p-2 border rounded"
                >
                  <option value="reminder">Reminder</option>
                  <option value="lease_start">Lease Start</option>
                  <option value="lease_end">Lease End</option>
                  <option value="rent_due">Rent Due</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="inspection">Inspection</option>
                  <option value="showing">Property Showing</option>
                  <option value="court_date">Court Date</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Date & Time</label>
                <input
                  type="datetime-local"
                  value={newEvent.start_date}
                  onChange={(e) => setNewEvent({...newEvent, start_date: e.target.value})}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Property</label>
                <select
                  value={newEvent.property_id}
                  onChange={(e) => {
                    setNewEvent({...newEvent, property_id: e.target.value, unit_id: ''});
                    fetchUnits(e.target.value);
                  }}
                  className="w-full p-2 border rounded"
                >
                  <option value="">Select Property</option>
                  {properties.map(property => (
                    <option key={property.id} value={property.id}>{property.name}</option>
                  ))}
                </select>
              </div>

              {newEvent.property_id && (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Unit</label>
                  <select
                    value={newEvent.unit_id}
                    onChange={(e) => setNewEvent({...newEvent, unit_id: e.target.value})}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Select Unit</option>
                    {units.map(unit => (
                      <option key={unit.id} value={unit.id}>{unit.unit_number}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                  className="w-full p-2 border rounded"
                  rows="3"
                />
              </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setShowEventModal(false)}
                    style={{
                      padding: '8px 16px',
                      color: '#6b7280',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Create Event
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Calendar;